import type { PipeTransform } from '@angular/core'
import { Pipe, inject } from '@angular/core'

import { AuthService } from '../../services/auth/auth.service'
import type { UserRole } from '../../types/types'

/**
 * Pipe to check if a user has specific roles
 *
 * Usage:
 *
 * - Check if user has any of the roles: 'admin' | hasRole
 * - Check if user has any of multiple roles: ['admin', 'billing-admin'] | hasRole
 */
@Pipe({
  name: 'hasRole',
  standalone: true,
  pure: false, // Make the pipe impure to update when the currentUser signal changes
})
export class HasRolePipe implements PipeTransform {
  private authService = inject(AuthService)

  /**
   * Check if the current user has the specified role(s)
   *
   * @param requiredRoles
   * @returns A boolean indicating if the user has the specified role(s)
   */
  transform(requiredRoles: UserRole | UserRole[]): boolean {
    const rolesToCheck = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles]
    const user = this.authService.currentUser()
    const userRoles = user?.roles ?? []

    if (rolesToCheck.length === 0) {
      return true
    }

    // Check if user has *at least one* of the specified roles
    return rolesToCheck.some(role => userRoles.includes(role))
  }
}
